import { useState, useEffect } from 'react'
import { billingApi, isPaginatedResponse } from '../services/api'
import type { SubscriptionPlan, Subscription, Invoice } from '../types/billing'
import {
  Check,
  X,
  CreditCard,
  TrendingUp,
  Users,
  Zap,
  AlertCircle,
  Crown,
  Sparkles,
  Receipt,
  ArrowRight,
  Shield,
  BarChart3,
  Layers,
} from 'lucide-react'

export default function Billing() {
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [usage, setUsage] = useState<{
    pipelines_count: number
    pipelines_limit: number
    leads_count: number
    leads_limit: number
    members_count: number
    members_limit: number
    automations_count: number
    automations_limit: number
    connections_count: number
    connections_limit: number
    webhooks_count: number
    webhooks_limit: number
  } | null>(null)
  const [plans, setPlans] = useState<SubscriptionPlan[]>([])
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'semestral' | 'anual'>('monthly')
  const [subscribingPlanId, setSubscribingPlanId] = useState<string | null>(null)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)

      const [subscriptionData, plansResponse, invoicesData] = await Promise.all([
        billingApi.getCurrent().catch(() => null),
        billingApi.listPlans(),
        billingApi.listInvoices().catch(() => []),
      ])

      setSubscription(subscriptionData)
      const plansArr = isPaginatedResponse<SubscriptionPlan>(plansResponse)
        ? plansResponse.results || []
        : Array.isArray(plansResponse)
          ? plansResponse
          : []
      setPlans(plansArr)

      // Invoices can also be paginated
      const invoicesArr = isPaginatedResponse<Invoice>(invoicesData)
        ? invoicesData.results || []
        : Array.isArray(invoicesData)
          ? invoicesData
          : []
      setInvoices(invoicesArr)

      if (subscriptionData) {
        try {
          const usageData = await billingApi.getUsage()
          setUsage(usageData)
        } catch (err) {
          console.error('Error loading usage:', err)
        }
      }
    } catch (err: any) {
      console.error('Error loading billing data:', err)
      setError(err.response?.data?.detail || 'Erro ao carregar dados de faturamento')
    } finally {
      setLoading(false)
    }
  }

  const handleSubscribe = async (planId: string) => {
    try {
      setError(null)
      setSubscribingPlanId(planId)
      const checkoutData = await billingApi.subscribe(planId, billingCycle)
      window.location.href = checkoutData.checkout_url
    } catch (err: any) {
      console.error('Error subscribing:', err)
      setError(err.response?.data?.detail || 'Erro ao processar assinatura')
      setSubscribingPlanId(null)
    }
  }

  const handleCancelSubscription = async () => {
    if (!confirm('Tem certeza que deseja cancelar sua assinatura? Ela permanecera ativa ate o fim do periodo atual.')) return

    try {
      setError(null)
      await billingApi.cancelSubscription()
      loadData()
    } catch (err: any) {
      console.error('Error canceling subscription:', err)
      setError(err.response?.data?.detail || 'Erro ao cancelar assinatura')
    }
  }

  const getStatusBadge = (status: string) => {
    const map: Record<string, { bg: string; color: string; label: string }> = {
      trialing: { bg: 'rgba(99, 102, 241, 0.15)', color: 'var(--color-primary-600)', label: 'Periodo de Teste' },
      active: { bg: 'rgba(16, 185, 129, 0.15)', color: 'var(--color-success-600)', label: 'Ativo' },
      canceled: { bg: 'rgba(239, 68, 68, 0.15)', color: 'var(--color-error-600)', label: 'Cancelado' },
      expired: { bg: 'rgba(148, 163, 184, 0.15)', color: 'var(--text-secondary)', label: 'Expirado' },
      past_due: { bg: 'rgba(245, 158, 11, 0.15)', color: 'var(--color-warning-600)', label: 'Pagamento Pendente' },
    }
    const s = map[status] || map.expired
    return (
      <span style={{
        background: s.bg,
        color: s.color,
        padding: '4px 12px',
        borderRadius: 'var(--radius-full)',
        fontSize: 'var(--text-xs)',
        fontWeight: 600,
      }}>
        {s.label}
      </span>
    )
  }

  const getInvoiceStatusBadge = (status: string) => {
    const map: Record<string, { bg: string; color: string; label: string }> = {
      paid: { bg: 'rgba(16, 185, 129, 0.15)', color: 'var(--color-success-600)', label: 'Pago' },
      open: { bg: 'rgba(245, 158, 11, 0.15)', color: 'var(--color-warning-600)', label: 'Em Aberto' },
      draft: { bg: 'rgba(148, 163, 184, 0.15)', color: 'var(--text-secondary)', label: 'Rascunho' },
      void: { bg: 'rgba(148, 163, 184, 0.15)', color: 'var(--text-secondary)', label: 'Anulada' },
      uncollectible: { bg: 'rgba(239, 68, 68, 0.15)', color: 'var(--color-error-600)', label: 'Incobr.' },
    }
    const s = map[status] || { bg: 'rgba(148, 163, 184, 0.15)', color: 'var(--text-secondary)', label: status }
    return (
      <span style={{
        background: s.bg,
        color: s.color,
        padding: '3px 10px',
        borderRadius: 'var(--radius-full)',
        fontSize: 'var(--text-xs)',
        fontWeight: 600,
      }}>
        {s.label}
      </span>
    )
  }

  const calculateUsagePercentage = (current: number, max: number) => {
    if (max === 0 || max === -1) return 0
    return Math.min((current / max) * 100, 100)
  }

  const getUsageBarColor = (percentage: number) => {
    if (percentage >= 90) return 'linear-gradient(90deg, #ef4444, #f87171)'
    if (percentage >= 70) return 'linear-gradient(90deg, #f59e0b, #fbbf24)'
    return 'linear-gradient(90deg, var(--color-primary-500), var(--color-secondary-500))'
  }

  const formatCurrency = (valueInCents: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(valueInCents / 100)
  }

  const getPlanPrice = (plan: SubscriptionPlan) => {
    switch (billingCycle) {
      case 'semestral':
        return plan.price_semestral
      case 'anual':
        return plan.price_anual
      default:
        return plan.price_monthly
    }
  }

  const getPlanIcon = (slug: string) => {
    if (slug === 'pro') return <Crown style={{ width: 24, height: 24 }} />
    if (slug === 'growth') return <TrendingUp style={{ width: 24, height: 24 }} />
    return <Zap style={{ width: 24, height: 24 }} />
  }

  const getPlanGradient = (slug: string, isPopular: boolean) => {
    if (isPopular) return 'linear-gradient(135deg, var(--color-primary-600) 0%, var(--color-secondary-600) 100%)'
    if (slug === 'pro') return 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
    return 'linear-gradient(135deg, var(--color-primary-50) 0%, var(--color-secondary-50) 100%)'
  }

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid var(--border-light)',
            borderTopColor: 'var(--color-primary-600)',
            borderRadius: '50%',
            animation: 'spin 0.6s linear infinite',
            margin: '0 auto var(--space-4)',
          }} />
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
            Carregando...
          </p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    )
  }

  return (
    <div style={{ animation: 'fadeIn 0.3s ease-out' }}>
      {/* Header */}
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-1)' }}>
          <CreditCard style={{ width: 28, height: 28, color: 'var(--color-primary-600)' }} />
          <h1 style={{
            fontSize: 'var(--text-2xl)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--text-primary)',
            margin: 0,
          }}>
            Faturamento
          </h1>
        </div>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)' }}>
          Gerencie sua assinatura, planos e faturas
        </p>
      </div>

      {/* Error Alert */}
      {error && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.08)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          borderRadius: 'var(--radius-xl)',
          padding: 'var(--space-4)',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 'var(--space-3)',
          marginBottom: 'var(--space-5)',
        }}>
          <AlertCircle style={{ width: 20, height: 20, color: 'var(--color-error-500)', flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-error-700)', fontWeight: 600, margin: 0 }}>Erro</p>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-error-600)', margin: '4px 0 0' }}>{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-error-500)', padding: 4 }}
          >
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>
      )}

      {/* Current Subscription Card */}
      {subscription && (
        <div style={{
          background: 'var(--glass-bg-strong)',
          backdropFilter: 'var(--glass-blur)',
          WebkitBackdropFilter: 'var(--glass-blur)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-2xl)',
          boxShadow: 'var(--shadow-glass), var(--shadow-glass-inset)',
          padding: 'var(--space-6)',
          marginBottom: 'var(--space-6)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-5)' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-2)' }}>
                <Shield style={{ width: 22, height: 22, color: 'var(--color-primary-600)' }} />
                <h2 style={{
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--text-primary)',
                  margin: 0,
                }}>
                  Assinatura Atual
                </h2>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                  Plano <strong style={{ color: 'var(--text-primary)' }}>{subscription.plan_name || subscription.plan_details?.name}</strong>
                </span>
                {getStatusBadge(subscription.status)}
                {subscription.cancel_at_period_end && (
                  <span style={{
                    background: 'rgba(245, 158, 11, 0.15)',
                    color: 'var(--color-warning-600)',
                    padding: '4px 12px',
                    borderRadius: 'var(--radius-full)',
                    fontSize: 'var(--text-xs)',
                    fontWeight: 600,
                  }}>
                    Cancela ao fim do periodo
                  </span>
                )}
              </div>
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', marginTop: 'var(--space-2)' }}>
                {subscription.billing_cycle === 'monthly' ? 'Mensal' : subscription.billing_cycle === 'semestral' ? 'Semestral' : 'Anual'}
                {' \u2022 '}
                Proxima cobranca: {subscription.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString('pt-BR') : 'N/A'}
              </p>
            </div>
            {subscription.current_price_display && (
              <div style={{ textAlign: 'right' }}>
                <div style={{
                  fontSize: 'var(--text-2xl)',
                  fontWeight: 'var(--font-weight-bold)',
                  background: 'var(--bg-gradient)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>
                  {subscription.current_price_display}
                </div>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                  /{subscription.billing_cycle === 'monthly' ? 'mes' : subscription.billing_cycle === 'semestral' ? 'semestre' : 'ano'}
                </span>
              </div>
            )}
          </div>

          {/* Usage Stats */}
          {usage && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 'var(--space-4)',
            }}>
              {[
                { icon: <TrendingUp style={{ width: 18, height: 18 }} />, label: 'Leads', count: usage.leads_count, limit: usage.leads_limit },
                { icon: <Layers style={{ width: 18, height: 18 }} />, label: 'Pipelines', count: usage.pipelines_count, limit: usage.pipelines_limit },
                { icon: <Users style={{ width: 18, height: 18 }} />, label: 'Usuarios', count: usage.members_count, limit: usage.members_limit },
              ].map((metric) => {
                const pct = calculateUsagePercentage(metric.count, metric.limit)
                return (
                  <div key={metric.label} style={{
                    background: 'var(--glass-bg-subtle)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: 'var(--radius-xl)',
                    padding: 'var(--space-4)',
                    border: '1px solid var(--glass-border)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-2)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', color: 'var(--color-primary-600)' }}>
                        {metric.icon}
                        <span style={{ fontWeight: 600, fontSize: 'var(--text-sm)', color: 'var(--text-primary)' }}>
                          {metric.label}
                        </span>
                      </div>
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', fontWeight: 500 }}>
                        {metric.count} / {metric.limit === -1 ? '\u221E' : metric.limit.toLocaleString('pt-BR')}
                      </span>
                    </div>
                    {metric.limit !== -1 && (
                      <div style={{
                        width: '100%',
                        height: '6px',
                        background: 'var(--border-light)',
                        borderRadius: 'var(--radius-full)',
                        overflow: 'hidden',
                      }}>
                        <div style={{
                          width: `${pct}%`,
                          height: '100%',
                          background: getUsageBarColor(pct),
                          borderRadius: 'var(--radius-full)',
                          transition: 'width 0.5s ease',
                        }} />
                      </div>
                    )}
                    {metric.limit === -1 && (
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-success-600)', fontWeight: 500 }}>
                        Ilimitado
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {/* Cancel button */}
          {(subscription.status === 'active' || subscription.status === 'trialing') && !subscription.cancel_at_period_end && (
            <div style={{ marginTop: 'var(--space-5)', paddingTop: 'var(--space-4)', borderTop: '1px solid var(--border-light)' }}>
              <button
                onClick={handleCancelSubscription}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--color-error-500)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 500,
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                Cancelar Assinatura
              </button>
            </div>
          )}
        </div>
      )}

      {/* No subscription alert */}
      {!subscription && (
        <div style={{
          background: 'rgba(99, 102, 241, 0.08)',
          border: '1px solid rgba(99, 102, 241, 0.2)',
          borderRadius: 'var(--radius-xl)',
          padding: 'var(--space-4) var(--space-5)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-3)',
          marginBottom: 'var(--space-6)',
        }}>
          <Sparkles style={{ width: 20, height: 20, color: 'var(--color-primary-500)' }} />
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-primary-700)', margin: 0 }}>
            Voce ainda nao possui uma assinatura. Escolha um plano abaixo para comecar!
          </p>
        </div>
      )}

      {/* Plans Section */}
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-5)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <BarChart3 style={{ width: 22, height: 22, color: 'var(--color-primary-600)' }} />
            <h2 style={{
              fontSize: 'var(--text-lg)',
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--text-primary)',
              margin: 0,
            }}>
              Planos Disponiveis
            </h2>
          </div>

          {/* Billing Cycle Toggle */}
          <div style={{
            display: 'flex',
            background: 'var(--glass-bg)',
            backdropFilter: 'var(--glass-blur)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-xl)',
            padding: '3px',
            gap: '2px',
          }}>
            {([
              { value: 'monthly' as const, label: 'Mensal' },
              { value: 'semestral' as const, label: 'Semestral' },
              { value: 'anual' as const, label: 'Anual' },
            ]).map(opt => (
              <button
                key={opt.value}
                onClick={() => setBillingCycle(opt.value)}
                style={{
                  padding: '6px 16px',
                  borderRadius: 'var(--radius-lg)',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 600,
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  background: billingCycle === opt.value ? 'var(--bg-primary)' : 'transparent',
                  color: billingCycle === opt.value ? 'var(--text-primary)' : 'var(--text-tertiary)',
                  boxShadow: billingCycle === opt.value ? 'var(--shadow-sm)' : 'none',
                }}
              >
                {opt.label}
                {opt.value === 'anual' && (
                  <span style={{
                    marginLeft: 6,
                    color: 'var(--color-success-600)',
                    fontSize: 'var(--text-2xs)',
                  }}>
                    -20%
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Plan Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 'var(--space-5)',
        }}>
          {plans.map((plan) => {
            const price = getPlanPrice(plan)
            const isCurrentPlan = subscription?.plan_details?.id === plan.id || subscription?.plan === plan.id as any
            const isPopular = plan.is_popular

            return (
              <div
                key={plan.id}
                style={{
                  background: 'var(--glass-bg-strong)',
                  backdropFilter: 'var(--glass-blur)',
                  WebkitBackdropFilter: 'var(--glass-blur)',
                  border: isPopular ? '2px solid var(--color-primary-400)' : '1px solid var(--glass-border)',
                  borderRadius: 'var(--radius-2xl)',
                  boxShadow: isPopular ? 'var(--shadow-primary)' : 'var(--shadow-glass), var(--shadow-glass-inset)',
                  overflow: 'hidden',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                }}
                className="plan-card"
              >
                {/* Popular Badge */}
                {isPopular && (
                  <div style={{
                    background: 'var(--bg-gradient)',
                    color: 'white',
                    textAlign: 'center',
                    padding: '6px',
                    fontSize: 'var(--text-xs)',
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                  }}>
                    MAIS POPULAR
                  </div>
                )}

                <div style={{ padding: 'var(--space-6)' }}>
                  {/* Plan Header */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-3)',
                    marginBottom: 'var(--space-4)',
                  }}>
                    <div style={{
                      width: 44,
                      height: 44,
                      borderRadius: 'var(--radius-xl)',
                      background: getPlanGradient(plan.slug, !!isPopular),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: isPopular || plan.slug === 'pro' ? 'white' : 'var(--color-primary-600)',
                    }}>
                      {getPlanIcon(plan.slug)}
                    </div>
                    <div>
                      <h3 style={{
                        fontSize: 'var(--text-lg)',
                        fontWeight: 'var(--font-weight-bold)',
                        color: 'var(--text-primary)',
                        margin: 0,
                      }}>
                        {plan.name}
                      </h3>
                      <p style={{
                        fontSize: 'var(--text-xs)',
                        color: 'var(--text-tertiary)',
                        margin: 0,
                      }}>
                        {plan.description}
                      </p>
                    </div>
                  </div>

                  {/* Price */}
                  <div style={{ marginBottom: 'var(--space-5)' }}>
                    <span style={{
                      fontSize: 'var(--text-3xl)',
                      fontWeight: 'var(--font-weight-black)',
                      color: 'var(--text-primary)',
                      letterSpacing: 'var(--tracking-tight)',
                    }}>
                      {formatCurrency(price)}
                    </span>
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)', marginLeft: 4 }}>
                      /{billingCycle === 'monthly' ? 'mes' : billingCycle === 'semestral' ? 'semestre' : 'ano'}
                    </span>
                  </div>

                  {/* Resources */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--space-3)',
                    marginBottom: 'var(--space-5)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                      <Users style={{ width: 16, height: 16, color: 'var(--color-primary-500)' }} />
                      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                        {plan.max_members === -1 ? 'Usuarios ilimitados' : `${plan.max_members} usuarios`}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                      <TrendingUp style={{ width: 16, height: 16, color: 'var(--color-primary-500)' }} />
                      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                        {plan.max_leads === -1 ? 'Leads ilimitados' : `${plan.max_leads.toLocaleString('pt-BR')} leads`}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                      <Layers style={{ width: 16, height: 16, color: 'var(--color-primary-500)' }} />
                      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                        {plan.max_pipelines === -1 ? 'Pipelines ilimitados' : `${plan.max_pipelines} pipelines`}
                      </span>
                    </div>
                  </div>

                  {/* Features List */}
                  {plan.features && plan.features.length > 0 && (
                    <div style={{
                      borderTop: '1px solid var(--border-light)',
                      paddingTop: 'var(--space-4)',
                      marginBottom: 'var(--space-5)',
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                        {plan.features.map((feature: string, index: number) => (
                          <div key={index} style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)' }}>
                            <Check style={{
                              width: 16,
                              height: 16,
                              color: 'var(--color-success-500)',
                              flexShrink: 0,
                              marginTop: 2,
                            }} />
                            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
                              {feature}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* CTA Button */}
                  <button
                    onClick={() => handleSubscribe(String(plan.id))}
                    disabled={!!subscribingPlanId || isCurrentPlan}
                    style={{
                      width: '100%',
                      padding: 'var(--space-3) var(--space-4)',
                      borderRadius: 'var(--radius-xl)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 600,
                      border: 'none',
                      cursor: isCurrentPlan ? 'default' : 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 'var(--space-2)',
                      background: isCurrentPlan
                        ? 'var(--bg-tertiary)'
                        : isPopular
                          ? 'var(--bg-gradient)'
                          : 'var(--glass-bg)',
                      color: isCurrentPlan
                        ? 'var(--text-tertiary)'
                        : isPopular
                          ? 'white'
                          : 'var(--text-primary)',
                      boxShadow: isPopular && !isCurrentPlan ? 'var(--shadow-primary)' : 'none',
                      backdropFilter: !isPopular ? 'var(--glass-blur)' : 'none',
                      opacity: subscribingPlanId && subscribingPlanId !== plan.id ? 0.5 : 1,
                    }}
                  >
                    {subscribingPlanId === plan.id ? (
                      <>
                        <div style={{
                          width: 16,
                          height: 16,
                          border: '2px solid currentColor',
                          borderTopColor: 'transparent',
                          borderRadius: '50%',
                          animation: 'spin 0.6s linear infinite',
                        }} />
                        Redirecionando...
                      </>
                    ) : isCurrentPlan ? (
                      'Plano Atual'
                    ) : (
                      <>
                        {subscription ? 'Fazer Upgrade' : 'Comecar Agora'}
                        <ArrowRight style={{ width: 16, height: 16 }} />
                      </>
                    )}
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Invoices Section */}
      {invoices.length > 0 && (
        <div style={{
          background: 'var(--glass-bg-strong)',
          backdropFilter: 'var(--glass-blur)',
          WebkitBackdropFilter: 'var(--glass-blur)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-2xl)',
          boxShadow: 'var(--shadow-glass), var(--shadow-glass-inset)',
          overflow: 'hidden',
        }}>
          <div style={{
            padding: 'var(--space-5) var(--space-6)',
            borderBottom: '1px solid var(--border-light)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
          }}>
            <Receipt style={{ width: 20, height: 20, color: 'var(--color-primary-600)' }} />
            <h2 style={{
              fontSize: 'var(--text-lg)',
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--text-primary)',
              margin: 0,
            }}>
              Faturas
            </h2>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                  {['Fatura', 'Periodo', 'Valor', 'Status', 'Pago em', ''].map((header) => (
                    <th
                      key={header}
                      style={{
                        textAlign: 'left',
                        padding: 'var(--space-3) var(--space-4)',
                        fontSize: 'var(--text-xs)',
                        fontWeight: 600,
                        color: 'var(--text-tertiary)',
                        textTransform: 'uppercase' as const,
                        letterSpacing: '0.05em',
                      }}
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr
                    key={invoice.id}
                    style={{ borderBottom: '1px solid var(--border-light)' }}
                    className="invoice-row"
                  >
                    <td style={{ padding: 'var(--space-3) var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--text-primary)', fontWeight: 500 }}>
                      #{invoice.stripe_invoice_id ? invoice.stripe_invoice_id.slice(-8) : invoice.id.toString().slice(-6)}
                    </td>
                    <td style={{ padding: 'var(--space-3) var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                      {invoice.period_start ? new Date(invoice.period_start).toLocaleDateString('pt-BR') : '-'}
                      {' - '}
                      {invoice.period_end ? new Date(invoice.period_end).toLocaleDateString('pt-BR') : '-'}
                    </td>
                    <td style={{ padding: 'var(--space-3) var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--text-primary)', fontWeight: 600 }}>
                      {invoice.amount_due_display || formatCurrency(invoice.amount_due || 0)}
                    </td>
                    <td style={{ padding: 'var(--space-3) var(--space-4)' }}>
                      {getInvoiceStatusBadge(invoice.status)}
                    </td>
                    <td style={{ padding: 'var(--space-3) var(--space-4)', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                      {invoice.paid_at ? new Date(invoice.paid_at).toLocaleDateString('pt-BR') : '-'}
                    </td>
                    <td style={{ padding: 'var(--space-3) var(--space-4)' }}>
                      {invoice.invoice_pdf && (
                        <a
                          href={invoice.invoice_pdf}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            fontSize: 'var(--text-xs)',
                            color: 'var(--color-primary-600)',
                            fontWeight: 500,
                            textDecoration: 'none',
                          }}
                        >
                          PDF
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .plan-card:hover {
          transform: translateY(-4px);
          box-shadow: var(--shadow-glass-lg), var(--shadow-glass-inset);
        }
        .invoice-row:hover {
          background: var(--bg-tertiary);
        }
        @media (max-width: 900px) {
          .plan-card {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  )
}
